import { useMemo } from "react";
import type { BalanceSnapshot } from "@capture/shared";
import { useStationStore } from "../state/station-store";
import { deriveTransactionLive, type LiveDerivation } from "../lib/live-balance";

/**
 * F-013 — the live derivation MUST come through these hooks, never through the
 * store's `livePolicyOutcome`/`liveBalance` getters at render time. Those are
 * stable `get()`-based functions: selecting one does not subscribe to the state
 * it reads, so a keying-pane `setDocAmount` push never re-renders the consumer
 * and the Σ/Δ readout freezes (the F-013 bug). These hooks subscribe to every
 * input the derivation reads (`payload` + `amountOverrides` + `allocationDraft`)
 * so the subscription can't be forgotten, and memoize the derive per render.
 *
 * The store getters remain for imperative (event-handler) callers only.
 */

/** All live derivations for the loaded batch, keyed by transaction id. Built
 *  once per render so the header rollup, rail chips, and active panel share the
 *  same work instead of each re-deriving every transaction. */
export function useLiveDerivations(): Map<string, LiveDerivation> {
  const payload = useStationStore((s) => s.payload);
  const amountOverrides = useStationStore((s) => s.amountOverrides);
  const allocationDraft = useStationStore((s) => s.allocationDraft);

  return useMemo(() => {
    const out = new Map<string, LiveDerivation>();
    if (!payload) return out;
    for (const txn of payload.transactions) {
      const rows = allocationDraft[txn.id] ?? txn.allocations ?? [];
      out.set(txn.id, deriveTransactionLive(txn, amountOverrides, payload.config, rows));
    }
    return out;
  }, [payload, amountOverrides, allocationDraft]);
}

/** The live derivation for a single transaction (the active keying panel). */
export function useLiveDerivation(txnId: string): LiveDerivation | null {
  return useLiveDerivations().get(txnId) ?? null;
}

/** Convenience: just the balance snapshot for a transaction. */
export function useLiveBalance(txnId: string): BalanceSnapshot | null {
  return useLiveDerivation(txnId)?.snapshot ?? null;
}
